import styled from 'styled-components';
import { useState } from 'react';

import { Button } from 'shared/components/Button';
import { Stack } from 'shared/components/Stack';
import { variableFontRegular } from 'styles/theme/text';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { useCurrency } from 'shared/hooks/useCurrency';
import { t } from 'localization/utils/t';

const TP = 'productTemplate.components.buyBar.OfferSection.OfferSectionListItem';

interface IOfferSectionListItem {
  topOfferPrice: number;
  userOfferPrice?: number;
  lastSalePrice?: number;
  isSneaker: boolean;
  isLoading?: boolean;
  handleOnClick: () => void;
}

export const OfferSectionListItem = ({
  topOfferPrice,
  userOfferPrice,
  lastSalePrice,
  isSneaker,
  handleOnClick,
  isLoading,
}: IOfferSectionListItem) => {
  const { selectedCurrencyCode } = useCurrency();
  const [isHoveringRow, setIsHoveringRow] = useState<boolean>(false);

  const userHasOffer = Boolean(userOfferPrice);
  const userHasTopOffer = typeof userOfferPrice === 'number' && userOfferPrice === topOfferPrice;

  const yourOfferCopy = t(`${TP}.yourOffer`, 'Your Offer');
  const topOfferCopy = t(`${TP}.topOffer`, 'Top Offer');
  const conditionCopy = isSneaker ? t(`${TP}.newInBox`, 'New in Box') : t(`${TP}.new`, 'New');
  const lastSaleCopy = t(`${TP}.lastSale`, 'Last Sale');
  const displayYourOfferCopy = `${yourOfferCopy} / ${conditionCopy}`;
  const displayTopOfferCopy = userHasTopOffer
    ? displayYourOfferCopy
    : `${topOfferCopy} / ${conditionCopy}`;
  const offerDescriptionCopy = (() => {
    if (!userHasOffer) {
      if (selectedCurrencyCode === 'USD') {
        return t(`${TP}.setThePrice`, 'Set the price you’re willing to pay.');
      }

      return t(
        `${TP}.setThePriceUsd`,
        'Set the price you’re willing to pay. Offers will transact in USD',
      );
    }
    if (userHasTopOffer) {
      return t(`${TP}.haveHighestOffer`, 'You have the highest offer');
    }
    return null;
  })();

  const handleRowMouseLeave = () => {
    setIsHoveringRow(false);
  };

  const handleRowMouseEnter = () => {
    setIsHoveringRow(true);
  };

  return (
    <OfferSectionListItem.TableRow
      onMouseEnter={handleRowMouseEnter}
      onMouseLeave={handleRowMouseLeave}
    >
      <OfferSectionListItem.PriceContainer>
        <Stack direction="column" spacing={8}>
          {userHasOffer && !userHasTopOffer && (
            <OfferSectionListItem.Hover>
              <LocalizedCurrency price={userOfferPrice} data-qa="user_offer_price" />
            </OfferSectionListItem.Hover>
          )}
          {topOfferPrice ? (
            <OfferSectionListItem.Hover>
              <LocalizedCurrency price={topOfferPrice} data-qa="top_offer_price" />
            </OfferSectionListItem.Hover>
          ) : (
            <OfferSectionListItem.Line />
          )}
          {lastSalePrice ? (
            <LocalizedCurrency price={lastSalePrice} data-qa="last_sale_price" />
          ) : (
            <OfferSectionListItem.Line />
          )}
        </Stack>
      </OfferSectionListItem.PriceContainer>
      <OfferSectionListItem.Condition data-qa="top_offer_condition">
        <Stack direction="column" spacing={8}>
          {userHasOffer && !userHasTopOffer && (
            <OfferSectionListItem.Hover data-qa="your_offer_copy">
              {displayYourOfferCopy}
            </OfferSectionListItem.Hover>
          )}
          <OfferSectionListItem.Hover data-qa="top_offer_copy">
            {displayTopOfferCopy}
          </OfferSectionListItem.Hover>
          <OfferSectionListItem.NoHover data-qa="last_sale_copy">
            {lastSaleCopy}
          </OfferSectionListItem.NoHover>
        </Stack>
      </OfferSectionListItem.Condition>
      <OfferSectionListItem.Description data-qa="top_offer_description">
        {offerDescriptionCopy}
      </OfferSectionListItem.Description>
      <OfferSectionListItem.ButtonContainer>
        <OfferSectionListItem.Button
          data-qa="make_offer_button"
          inverted={!isHoveringRow}
          onClick={handleOnClick}
          loading={isLoading}
          title={userHasOffer ? t(`${TP}.edit`, 'Edit') : t(`${TP}.offer`, 'Offer')}
        />
      </OfferSectionListItem.ButtonContainer>
    </OfferSectionListItem.TableRow>
  );
};

OfferSectionListItem.NoHover = styled.span`
  border-bottom: 1px solid transparent;
`;

OfferSectionListItem.Hover = styled.span`
  border-bottom: 1px solid transparent;
`;

// This dashed line is very true to the design. Font and all.
OfferSectionListItem.Line = styled.span`
  ${variableFontRegular({ height: '20px', size: '11px' })};
  text-align: center;
  min-width: 18px;
  font-weight: 400;
  border-bottom: 1px solid transparent;
  &:before {
    content: '----';
  }
`;

OfferSectionListItem.TableRow = styled.tr`
  ${variableFontRegular({ height: '20px', size: '12px' })};
  border-bottom: ${({ theme }) => theme.borders.white};
  vertical-align: top;
  cursor: pointer;
  // Only the first element should underline on hover.
  // TODO: Make this consistent with the Buy New Section
  &:hover ${OfferSectionListItem.Hover}:first-child {
    text-decoration: underline;
  }
`;

// Note: <td> doesnt obey `min-width` by nature, but `width: 0;` forces it to stay as small as possible.
OfferSectionListItem.PriceContainer = styled.td`
  width: 0;
  padding-right: 24px;
  padding-bottom: 16px;
`;

OfferSectionListItem.Condition = styled.td`
  width: 0;
  padding-right: 24px;
  padding-bottom: 16px;
  white-space: nowrap;
`;

OfferSectionListItem.Description = styled.td`
  ${variableFontRegular({ height: '20px', size: '10px' })};
  padding-right: 24px;
  padding-bottom: 16px;
`;

OfferSectionListItem.ButtonContainer = styled.td`
  width: 0;
  padding-bottom: 16px;
`;

OfferSectionListItem.Button = styled(Button)`
  width: auto;
  height: 20px;
  ${variableFontRegular({ size: '11.5px' })}
  padding: 3px;
  margin-left: auto;
  white-space: nowrap;
  text-transform: capitalize;
  border: ${({ theme }) => theme.borders.black};
  &:active {
    border: ${({ theme }) => theme.borders.black};
  }

  ${Button.LoaderWrapper} {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
    padding: 16px 0;
  }

  ${Button.Loader} {
    min-width: 32px;
  }
`;
